// React
// MUI
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { GridColDef } from "@mui/x-data-grid-pro";

// Hooks
import useSummaryTransformedRows from "@DataPages/hooks/inventory/reconciliation/useSummaryTransformedRows";

// Components
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import TypographyCell from "@Layouts/components/Tables/DataSmartTable/additional/TypographyCell";

// Helpers
import { isFooterCell } from "@Common/helpers/Tables";
import { getCellClasses } from "@Layouts/components/Tables/DataSmartTable/helpers";

// Constants
import { propertiesMap } from "@DataPages/components/Inventory/Tables/Reconciliation/SummaryTable/constants";

// Types
import { TReconciliationSummaryPropertyRow } from "@DataPages/types/inventory/commons";
import { ISummaryDetail } from "@DataPages/types/inventory/models/reconciliation";

type CommonInformationSummaryDetail = Pick<
	ISummaryDetail,
	| "physicalInventoryCountOnLot"
	| "physicalInventoryCountOffLot"
	| "unitsOnScheduleNotOnLot"
	| "unitsOnLotNotOnSchedule"
	| "unitsOffLotNotOnSchedule"
	| "totalSchedule"
	| "perGeneralLedgerSchedule"
	| "differenceSchedule"
>;
type CommonInformationTableProps = Readonly<{
	data: CommonInformationSummaryDetail;
}>;

const propertyRows: TReconciliationSummaryPropertyRow<CommonInformationSummaryDetail>[] = [
	{
		label: "physicalInventoryCountOnLot",
		idx: 1,
	},
	{
		label: "physicalInventoryCountOffLot",
		idx: 2,
	},
	{
		label: "unitsOnScheduleNotOnLot",
		idx: 3,
	},
	{
		label: "unitsOnLotNotOnSchedule",
		idx: 4,
	},
	{
		label: "unitsOffLotNotOnSchedule",
		idx: 5,
	},
	{
		label: "totalSchedule",
		idx: 6,
	},
	{
		label: "perGeneralLedgerSchedule",
		idx: 7,
	},
	{
		label: "differenceSchedule",
		idx: 8,
		isFooter: true,
	},
];

const columns: GridColDef[] = [
	{
		field: "_id",
		headerName: "",
	},
	{
		field: "key",
		headerName: "",
		sortable: false,
		flex: 3,
		cellClassName: (params) =>
			getCellClasses(
				"",
				{
					isSubTotalRow: params.row?.key === propertiesMap["totalSchedule"],
					isFooter: params.row?.isFooter ?? isFooterCell(params) ?? false,
					noWrap: true,
				},
				params
			),
		renderCell: (params) => {
			if (isFooterCell(params)) {
				return (
					<TypographyCell params={params} fontFamily={"Montserrat"} fontSize={"14px"}>
						Difference (Should Equal Zero):
					</TypographyCell>
				);
			}
			return (
				<TypographyCell
					params={params}
					fontWeight={params.row?.key === propertiesMap["totalSchedule"] || params.row?.isFooter ? "500" : "700"}
					fontSize={params.row?.key === propertiesMap["totalSchedule"] || params.row?.isFooter ? "14px" : "12px"}
					hints={{
						isSubTotalRow: params.row?.key === propertiesMap["totalSchedule"],
						isFooter: params.row?.isFooter ?? false,
					}}
				/>
			);
		},
	},
	{
		field: "value",
		headerName: "",
		flex: 1,
		align: "right",
		type: "number",
		sortable: false,
		renderCell: (params) => {
			return (
				<TypographyCell
					params={params}
					hints={{ isSubTotalRow: params.row?.key === propertiesMap["totalManufacture"], isFooter: params.row?.isFooter }}
				/>
			);
		},
		cellClassName: (params) =>
			getCellClasses(
				"",
				{
					isSubTotalRow: params.row?.key === propertiesMap["totalManufacture"],
					noWrap: true,

					isFooter: params.row?.isFooter ?? isFooterCell(params) ?? false,
				},
				params
			),
	},
];

export default function CommonInformationTable(props: CommonInformationTableProps) {
	const apiRef = useGridApiRef();

	const commonSummaryTableTransformedRows = useSummaryTransformedRows({
		propertyRows,
		data: props.data,
	});

	return (
		<DataSmartTable
			apiRef={apiRef}
			rowHeight={25}
			headerHeight={20}
			rows={commonSummaryTableTransformedRows}
			columns={columns}
			disableColumnMenu
			disableRowSelectionOnClick
			aggregationModel={{
				value: "exclusiveSummation",
			}}
			columnVisibilityModel={{
				_id: false,
			}}
			customProps={{
				tableClassName: {
					"functional-table footer-table-included": true,
					"mb-[-10px]": true,
				},
			}}
		/>
	);
}
